// Ambassador: Yatharth.
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import { isMoment } from 'moment-timezone';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { errorToast } from '../toast';
import { areJavascriptDatesEqual, isJavascriptDate } from './datetimes/javascriptDates';
import { validateEmail, validatePhone } from './validation';

export const polymorphicIsEqual = (obj1: unknown, obj2: unknown): boolean => {
    if (isJavascriptDate(obj1) && isJavascriptDate(obj2)) {
        return areJavascriptDatesEqual(obj1, obj2);
    } else if (isMoment(obj1) && isMoment(obj2)) {
        return obj1.isSame(obj2);
    } else if (
        typeof obj1 === 'object' &&
        obj1 !== null &&
        typeof obj2 === 'object' &&
        obj2 !== null
    ) {
        if (Object.keys(obj1).length !== Object.keys(obj2).length) {
            return false;
        }
        // @ts-expect-error Typescript doesn't know if the key access is safe.
        return Object.keys(obj1).every((key) => polymorphicIsEqual(obj1[key], obj2[key]));
    } else {
        // Comparing objects doesn’t work in Javascript, so we use lodash instead.
        //  See: https://stackoverflow.com/a/3198202.
        return _.isEqual(obj1, obj2);
    }
};

export const polymorphicIsNotEmpty = (obj: unknown): boolean => {
    if (obj === undefined) {
        // Undefined is the empty value for optional inputs.
        return false;
    } else if (obj instanceof Set) {
        return obj.size > 0;
    } else if (obj instanceof Array) {
        return obj.length > 0;
    } else if (typeof obj === 'string') {
        return obj.length > 0;
    } else if (typeof obj === 'boolean') {
        // For boolean states, all values including false should be considered a valid, non-empty value.
        return true;
    } else if (typeof obj === 'number') {
        // For numeric states, all values including 0 should be considered a valid, non-empty value,
        //  except NaN and infinity.
        return Number.isFinite(obj);
    } else if (typeof obj === 'object') {
        // If there is an object, it would be used in two ways:
        // - The non-empty value is an object like { uuid: ..., name: ...} and the empty value is null.
        // - The non-empty value is something like { key1: value1, key2: value2 } and the empty value is {}.
        // To support both cases, we consider the object non-empty if it has any keys.

        // NOTE: Javascript is weird. The type of `null` is also 'object'.
        //  Hence, we have to do a null check explicitly.

        return obj !== null && Object.keys(obj).length > 0;
    } else {
        // Uh oh, don’t know what to do. Log an error, but do our best to guess.
        // We use captureException() instead of captureMessage() as this way,
        //  we get a stack trace included.
        Sentry.captureException(
            new Error(`polymorphicIsNotEmpty() called with unknown type: ${typeof obj}`),
        );
        return !!obj;
    }
};

//TODO: We need to make this more flexible.
// QHP Basic was added to the list when supporting the teams as an input value,
// but it was a quick fix and we don't want to keep adding types to this list.
// Update: Yatharth: I give up, just making this accept anything.
export type AllowedInputValueItem = object | string | number | boolean | null | undefined;
// export type AllowedInputValueItem =
//     | string
//     | number
//     | boolean
//     | Moment
//     | string[]
//     | Record<string, string>
//     | Record<string, Moment>
//     | Record<string, string | null>
//     | QhpSimple
//     | AssignmentAndTimes
//     | DayOfWeekTimeslot
//     | AxleTime
//     | VisitPositionTentativeAssignment
//     | VisitForList
//     | VisitService
//     | VisitService[]
//     | undefined;
export type AllowedInputValue<InputValueItem> =
    | InputValueItem
    | (InputValueItem | null)
    | InputValueItem[]
    | Set<InputValueItem>
    | Record<string, InputValueItem>
    | Record<string, InputValueItem>[]
    | Record<string, InputValueItem | null>;

type ValidationError = string | null;

export type AllowedInputState<T extends AllowedInputValueItem = AllowedInputValueItem> =
    AllowedInputValue<T>;

type Transformer<InputValue extends AllowedInputValue<AllowedInputValueItem>> = (
    oldValue: InputValue,
) => {
    value: InputValue;
    error: ValidationError;
};
type Validator<InputValue extends AllowedInputValue<AllowedInputValueItem>> = (
    newValue: InputValue,
) => ValidationError;
type OnChangeCallback<InputValue extends AllowedInputValue<AllowedInputValueItem>> = (
    newValue: InputValue,
) => void;

const checkValid_ = ({
    isValid,
    setIsTouched,
}: {
    isValid: boolean;
    setIsTouched: (isTouched: boolean) => void;
}): boolean => {
    if (!isValid) {
        setIsTouched(true);
        errorToast('Some fields are still invalid.');
        return false;
    } else {
        return true;
    }
};
const checkDifferent_ = ({
    isDifferent,
    doError = true,
}: {
    isDifferent: boolean;
    doError?: boolean;
}): boolean => {
    if (!isDifferent) {
        if (doError) {
            errorToast('You didn’t change any fields.');
        }
        return false;
    } else {
        return true;
    }
};

export const emailValidator: Validator<string> = (email: string) =>
    validateEmail(email) ? null : 'Please enter a valid email address.';
export const phoneValidator: Validator<string> = (phone: string) =>
    validatePhone(phone)
        ? null
        : 'Please enter a valid 10-digit US phone number, or an international phone ' +
          "number starting with a '+' and the country code. " +
          "Special characters like '.', '-', and '()' are not allowed.";

export const nonNegativeValidator: Validator<number | null> = (value: number | null) =>
    value !== null && value >= 0 ? null : 'Must be greater than 0.';

export const positiveValidator: Validator<number | null> = (value: number | null) =>
    value !== null && value > 0 ? null : 'Must be greater than 0.';

export const regexValidator =
    (regex: RegExp, errorMessage: string): Validator<string> =>
    (value: string) =>
        regex.test(value) ? null : errorMessage;

type InputStateOptions<InputValue extends AllowedInputValue<AllowedInputValueItem>> = {
    required?: boolean;
    resetValue?: InputValue;
    defaultHelperText?: string;
    disablePaddingAfter?: boolean;
    initialError?: ValidationError;
    validators?: Validator<InputValue>[];
    transformers?: Transformer<InputValue>[];
    onChangeCallbacks?: OnChangeCallback<InputValue>[];
};

export const useInputState = <T extends AllowedInputValue<AllowedInputValueItem>>(
    initialValue: T,
    {
        required = false,
        // If the value to reset the state to is different from its initial value, you can provide it.
        resetValue,
        defaultHelperText = '',
        disablePaddingAfter = false,
        initialError = null,
        validators: initialValidators = [],
        transformers: initialTransformers = [],
        onChangeCallbacks: initialOnChangeCallbacks = [],
    }: InputStateOptions<T> = {},
): InputState<T> => {
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState(initialError);
    const isValid = error === null;

    // Whether the input has been "touched" by the user. Changing the value of
    //  a field or trying to submit the form it’s part of counts as touching the field.
    const [isTouched, setIsTouched] = useState(false);
    // We only show errors once a field has been touched.
    const showError = isTouched && !isValid;
    // Using ' ' reserves a 1-line height for the helper text.
    const helperText = (showError ? error : defaultHelperText) || (disablePaddingAfter ? '' : '');

    // Whether the input is different that the initialValue.
    const isDifferent = !polymorphicIsEqual(value, initialValue);
    const valueIfDifferentElseUndefined = isDifferent ? value : undefined;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const transformers = useMemo(() => initialTransformers, []);
    // const [transformers, setTransformers] = useState(initialTransformers);
    // const addTransformer = (newTransformer: Transformer<InputValue>) =>
    //     setTransformers((transformers) => [...transformers, newTransformer]);

    const allTransformersCombined: Transformer<T> = useCallback(
        (newValue) => {
            let newError: ValidationError = null;
            // Start with a null error, and run transformers until an error is found.
            for (const transformer of transformers) {
                ({ value: newValue, error: newError } = transformer(newValue));
                if (newError) {
                    break;
                }
            }
            return { value: newValue, error: newError };
        },
        [transformers],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const validators = useMemo(() => initialValidators, []);
    // const [validators, setValidators] = useState(initialValidators);
    // const addValidator = (newValidator: Validator<InputValue>) =>
    //     setValidators((validators) => [...validators, newValidator]);

    const allValidatorsCombined: Validator<T> = useCallback(
        (value) => {
            // If the field is required, check if it’s required.
            if (required) {
                if (!polymorphicIsNotEmpty(value)) {
                    return 'This field is required.';
                }
            }

            // If the value is empty, don’t run any other validators.
            //  Empty values are implicit considered valid.
            //  To avoid this, mark the value as required.
            if (!polymorphicIsNotEmpty(value)) {
                return null;
            }

            // Return the first validation error, or null if there are no errors.
            const validationResults = validators.map((validator) => validator(value));
            const firstValidationError = validationResults.find((result) => result);
            return firstValidationError ?? null;
        },
        [required, validators],
    );

    // This makes the validators run for the initial value, as well as whenever
    //  a new validator or transformer is added. Otherwise, the validators would
    //  only run when the value changes, which won’t work if the user never changed
    //  the value and just submitted the form, thereby skipping the validation.
    useEffect(() => {
        const validationError = allValidatorsCombined(value);
        if (validationError) {
            setError(validationError);
        }
        // We don’t want to run this effect when `value` changes;
        //  only when `validators` changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validators]);

    // Without the useMemo(), we sometimes get into infinite render loops,
    //  because some callers are naughty, and pass in a callback without
    //  wrapping it in useCallback(), which means it changes on every render,
    //  and we keep calling it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onChangeCallbacks = useMemo(() => initialOnChangeCallbacks, []);
    // const [onChangeCallbacks, setOnChangeCallbacks] = useState(initialOnChangeCallbacks);
    // const addOnChangeCallback = (newOnChangeCallback: OnChangeCallback<InputValue>) =>
    //     setOnChangeCallbacks((onChangeCallbacks) => [...onChangeCallbacks, newOnChangeCallback]);

    // Call the onChange callbacks for the initial value too.
    useEffect(() => {
        onChangeCallbacks.forEach((callback) => callback(value));
        // We only want this function to run when `onChangeCallbacks` changes, not when `value` changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onChangeCallbacks]);

    const setInputState = useCallback(
        (
            newValueOrValueFunc: T | ((oldValue: T) => T),
            {
                error: newError = null,
                isTouched: newIsTouched,
            }: {
                error?: ValidationError;
                isTouched?: boolean | undefined;
            } = {},
        ): void => {
            setValue((oldValue) => {
                const newUntransformedValue =
                    typeof newValueOrValueFunc === 'function'
                        ? (newValueOrValueFunc(oldValue) as T)
                        : newValueOrValueFunc;

                // Run transformers on the new value.
                const { value: newValue, error: transformationError } =
                    allTransformersCombined(newUntransformedValue);

                // If any of the transformers returned an error, set that as the error.
                if (transformationError !== null) {
                    setError(transformationError);
                } else {
                    // Assuming transformers did not raise an error, run all the validators.
                    //  If any of them return an error, set that as the error.
                    const validationError = allValidatorsCombined(newValue);
                    if (validationError !== null) {
                        setError(validationError);
                    } else {
                        // Assuming validation did not raise an error, let the caller of
                        //  setInputState() determine the new error.
                        setError(newError);
                    }
                }

                // If isTouched is undefined, the default behaviour is to mark the
                //  input as touched if the value has changed to a non-empty value.
                if (newIsTouched === undefined) {
                    if (polymorphicIsNotEmpty(newValue)) {
                        setIsTouched(true);
                    }
                } else {
                    // Otherwise, let the caller determined the new isTouched state.
                    setIsTouched(newIsTouched);
                }

                onChangeCallbacks.forEach((callback) => callback(newValue));
                return newValue;
            });
        },
        [allTransformersCombined, allValidatorsCombined, onChangeCallbacks],
    );

    const reset = () => {
        setInputState(resetValue === undefined ? initialValue : resetValue);
        setIsTouched(false);
    };

    const checkValid = () => checkValid_({ isValid, setIsTouched });
    const checkDifferent = () => checkDifferent_({ isDifferent });

    return {
        // Reading the state.
        value,
        initialValue,
        required,
        isValid,
        showError,
        helperText,
        isDifferent,
        valueIfDifferentElseUndefined,
        // Modifying the state.
        setInputState,
        setIsTouched,
        reset,
        // addTransformer,
        // addValidator,
        // addOnChangeCallback,
        // Form submission helpers.
        checkValid,
        checkDifferent,
    };
};

export type InputState<InputValue extends AllowedInputValue<AllowedInputValueItem>> = {
    // Reading the state.
    value: InputValue;
    initialValue: InputValue;
    required: boolean;
    isValid: boolean;
    showError: boolean;
    helperText: string;
    isDifferent: boolean;
    valueIfDifferentElseUndefined: InputValue | undefined;
    // Modifying the state.
    setInputState: (
        newValueOrValueFunc: InputValue | ((oldValue: InputValue) => InputValue),
        options?: {
            error?: ValidationError;
            isTouched?: boolean | undefined;
        },
    ) => void;
    setIsTouched: (isTouched: boolean) => void;
    reset: () => void;
    // Form submission helpers.
    checkValid: () => boolean;
    checkDifferent: () => boolean;
};

type JointInputState = {
    // Getting the state.
    isValid: boolean;
    showError: boolean;
    isDifferent: boolean;
    // Modify the state.
    setIsTouched: (isTouched: boolean) => void;
    reset: () => void;
    // Form submission helpers.
    checkValid: () => boolean;
    checkDifferent: (doError?: boolean) => boolean;
};

export const useJointInputState = <InputValue extends AllowedInputValue<AllowedInputValueItem>>(
    inputStates: (InputState<InputValue> | JointInputState)[],
): JointInputState => {
    const isValid = inputStates.every((inputState) => inputState.isValid);
    const showError = inputStates.some((inputState) => inputState.showError);
    const isDifferent = inputStates.some((inputState) => inputState.isDifferent);

    const setIsTouched = (isTouched: boolean) =>
        inputStates.forEach((inputState) => inputState.setIsTouched(isTouched));
    const reset = () => inputStates.forEach((inputState) => inputState.reset());

    const checkValid = () => checkValid_({ isValid, setIsTouched });
    const checkDifferent = (doError = true) => checkDifferent_({ isDifferent, doError });

    return {
        // Getting the state.
        isValid,
        showError,
        isDifferent,
        // Modify the state.
        setIsTouched,
        reset,
        // Form submission helpers.
        checkValid,
        checkDifferent,
    };
};

export const useTransformedInputState = <
    OldInputValue extends AllowedInputValue<AllowedInputValueItem>,
    NewInputValue extends AllowedInputValue<AllowedInputValueItem>,
>({
    inputState,
    transform,
    untransform,
}: {
    inputState: InputState<OldInputValue>;
    transform: (oldValue: OldInputValue) => NewInputValue;
    untransform: (newValue: NewInputValue) => OldInputValue;
}): InputState<NewInputValue> => {
    return {
        ...inputState,
        value: transform(inputState.value),
        valueIfDifferentElseUndefined:
            inputState.valueIfDifferentElseUndefined !== undefined
                ? transform(inputState.valueIfDifferentElseUndefined)
                : undefined,
        setInputState: (newValueOrValueFunc, options) => {
            const newValueFunc = (oldValue: OldInputValue) => {
                if (typeof newValueOrValueFunc === 'function') {
                    return untransform(newValueOrValueFunc(transform(oldValue)) as NewInputValue);
                } else {
                    return untransform(newValueOrValueFunc);
                }
            };
            inputState.setInputState(newValueFunc, options);
        },
        reset: () => {
            inputState.reset();
            // Let the caller know if the value was reset, otherwise they have no idea.
            transform(inputState.initialValue);
        },
        initialValue: transform(inputState.initialValue),
    };
};

// A static input state, that cannot be changed, but that can be passed into components
//  that expect an input state (like <AxleCheckboxField/>).
export const useStaticInputState = <InputValue extends AllowedInputValue<AllowedInputValueItem>>(
    initialValue: InputValue,
    options: InputStateOptions<InputValue> = {},
): InputState<InputValue> => {
    const dynamicInputState = useInputState(initialValue, options);
    const staticInputState = useTransformedInputState({
        inputState: dynamicInputState,
        transform: (value) => value,
        untransform: () => {
            throw new Error("Can't set a value on static input state.");
        },
    });
    return staticInputState;
};

// By not setting an input state to a value that it already is, it helps avoid
//  infinite rerender loops.
//
// You might not get infinite rerender loops if you only have one or two input states,
//  but in more complex conditions, they start showing up, and it's good to nip them early.
//
// Q: Why can't we have `.setInputState()` itself just early return if the value to be
//    set is the same as the current value?
// A: Because it is passing a function to `setState()`, which can cause a re-render event
//    if the new value is the same. See https://stackoverflow.com/questions/59489959/set-state-with-same-value-using-hooks-will-cause-a-rerender#comment131066082_73385958
export const setInputStateIfNotAlready = <Value extends AllowedInputValue<AllowedInputValueItem>>(
    inputState: InputState<Value>,
    value: Value,
): void => {
    if (inputState.value !== value) {
        inputState.setInputState(value);
    }
};

// Temporary helper to wrap a plain React state in an input state and allow it to be included
// in a joint input state.
//
// TODO: Remove this once we have a better way to handle array-style input states.
export const useInputStateFromState = <InputValue extends AllowedInputValue<AllowedInputValueItem>>(
    initialValue: InputValue,
    value: InputValue,
    setValue: Dispatch<SetStateAction<InputValue>>,
    {
        required = false,
        // If the value to reset the state to is different from its initial value, you can provide it.
        resetValue,
        defaultHelperText = '',
        disablePaddingAfter = false,
        initialError = null,
        validators: initialValidators = [],
        transformers: initialTransformers = [],
        onChangeCallbacks: initialOnChangeCallbacks = [],
    }: InputStateOptions<InputValue> = {},
) => {
    const [error, setError] = useState(initialError);
    const isValid = error === null;

    // Whether the input has been "touched" by the user. Changing the value of
    //  a field or trying to submit the form it’s part of counts as touching the field.
    const [isTouched, setIsTouched] = useState(false);
    // We only show errors once a field has been touched.
    const showError = isTouched && !isValid;
    // Using ' ' reserves a 1-line height for the helper text.
    const helperText = (showError ? error : defaultHelperText) || (disablePaddingAfter ? '' : '');

    // Whether the input is different that the initialValue.
    const isDifferent = !polymorphicIsEqual(value, initialValue);
    const valueIfDifferentElseUndefined = isDifferent ? value : undefined;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const transformers = useMemo(() => initialTransformers, []);
    // const [transformers, setTransformers] = useState(initialTransformers);
    // const addTransformer = (newTransformer: Transformer<InputValue>) =>
    //     setTransformers((transformers) => [...transformers, newTransformer]);

    const allTransformersCombined: Transformer<InputValue> = useCallback(
        (newValue) => {
            let newError: ValidationError = null;
            // Start with a null error, and run transformers until an error is found.
            for (const transformer of transformers) {
                ({ value: newValue, error: newError } = transformer(newValue));
                if (newError) {
                    break;
                }
            }
            return { value: newValue, error: newError };
        },
        [transformers],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const validators = useMemo(() => initialValidators, []);
    // const [validators, setValidators] = useState(initialValidators);
    // const addValidator = (newValidator: Validator<InputValue>) =>
    //     setValidators((validators) => [...validators, newValidator]);

    const allValidatorsCombined: Validator<InputValue> = useCallback(
        (value) => {
            // If the field is required, check if it’s required.
            if (required) {
                if (!polymorphicIsNotEmpty(value)) {
                    return 'This field is required.';
                }
            }

            // If the value is empty, don’t run any other validators.
            //  Empty values are implicit considered valid.
            //  To avoid this, mark the value as required.
            if (!polymorphicIsNotEmpty(value)) {
                return null;
            }

            // Return the first validation error, or null if there are no errors.
            const validationResults = validators.map((validator) => validator(value));
            const firstValidationError = validationResults.find((result) => result);
            return firstValidationError ?? null;
        },
        [required, validators],
    );

    // This makes the validators run for the initial value, as well as whenever
    //  a new validator or transformer is added. Otherwise, the validators would
    //  only run when the value changes, which won’t work if the user never changed
    //  the value and just submitted the form, thereby skipping the validation.
    useEffect(() => {
        const validationError = allValidatorsCombined(value);
        if (validationError) {
            setError(validationError);
        }
        // We don’t want to run this effect when `value` changes;
        //  only when `validators` changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validators]);

    // Without the useMemo(), we sometimes get into infinite render loops,
    //  because some callers are naughty, and pass in a callback without
    //  wrapping it in useCallback(), which means it changes on every render,
    //  and we keep calling it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onChangeCallbacks = useMemo(() => initialOnChangeCallbacks, []);
    // const [onChangeCallbacks, setOnChangeCallbacks] = useState(initialOnChangeCallbacks);
    // const addOnChangeCallback = (newOnChangeCallback: OnChangeCallback<InputValue>) =>
    //     setOnChangeCallbacks((onChangeCallbacks) => [...onChangeCallbacks, newOnChangeCallback]);

    // Call the onChange callbacks for the initial value too.
    useEffect(() => {
        onChangeCallbacks.forEach((callback) => callback(value));
        // We only want this function to run when `onChangeCallbacks` changes, not when `value` changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onChangeCallbacks]);

    const setInputState = useCallback(
        (
            newValueOrValueFunc: InputValue | ((oldValue: InputValue) => InputValue),
            {
                error: newError = null,
                isTouched: newIsTouched,
            }: {
                error?: ValidationError;
                isTouched?: boolean | undefined;
            } = {},
        ): void => {
            setValue((oldValue) => {
                const newUntransformedValue =
                    typeof newValueOrValueFunc === 'function'
                        ? (newValueOrValueFunc(oldValue) as InputValue)
                        : newValueOrValueFunc;

                // Run transformers on the new value.
                const { value: newValue, error: transformationError } =
                    allTransformersCombined(newUntransformedValue);

                // If any of the transformers returned an error, set that as the error.
                if (transformationError !== null) {
                    setError(transformationError);
                } else {
                    // Assuming transformers did not raise an error, run all the validators.
                    //  If any of them return an error, set that as the error.
                    const validationError = allValidatorsCombined(newValue);
                    if (validationError !== null) {
                        setError(validationError);
                    } else {
                        // Assuming validation did not raise an error, let the caller of
                        //  setInputState() determine the new error.
                        setError(newError);
                    }
                }

                // If isTouched is undefined, the default behaviour is to mark the
                //  input as touched if the value has changed to a non-empty value.
                if (newIsTouched === undefined) {
                    if (polymorphicIsNotEmpty(newValue)) {
                        setIsTouched(true);
                    }
                } else {
                    // Otherwise, let the caller determined the new isTouched state.
                    setIsTouched(newIsTouched);
                }

                onChangeCallbacks.forEach((callback) => callback(newValue));
                return newValue;
            });
        },
        [allTransformersCombined, allValidatorsCombined, onChangeCallbacks, setValue],
    );

    const reset = () => {
        setInputState(resetValue === undefined ? initialValue : resetValue);
        setIsTouched(false);
    };

    const checkValid = () => checkValid_({ isValid, setIsTouched });
    const checkDifferent = () => checkDifferent_({ isDifferent });

    return {
        // Reading the state.
        value,
        initialValue,
        required,
        isValid,
        showError,
        helperText,
        isDifferent,
        valueIfDifferentElseUndefined,
        // Modifying the state.
        setInputState,
        setIsTouched,
        reset,
        // addTransformer,
        // addValidator,
        // addOnChangeCallback,
        // Form submission helpers.
        checkValid,
        checkDifferent,
    };
};
